/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\DynamicContextEditModal.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the AI Integration module.
----------------------------------------------------------------*/


import { useState } from 'react';
import { X, Plus } from 'lucide-react';
import { Label } from '../../../components/ui/label';
import { Toggle } from '../../../components/ui/Toggle';
import { type DynamicContextInput } from '../api/aiAgentsService';

interface DynamicContextEditModalProps {
  isOpen: boolean;
  isEditing: boolean;
  formData: DynamicContextInput;
  keywordInput: string;
  setKeywordInput: (v: string) => void;
  setFormData: (fn: (prev: DynamicContextInput) => DynamicContextInput) => void;
  onSave: () => void;
  onClose: () => void;
  t: (key: string, fallback?: string) => string;
}

function estimateTokens(text: string): number {
  return Math.ceil((text || '').length / 4);
}

export function DynamicContextEditModal({
  isOpen, isEditing, formData, keywordInput, setKeywordInput,
  setFormData, onSave, onClose, t,
}: DynamicContextEditModalProps) {
  if (!isOpen) return null;

  const handleAddKeyword = () => {
    if (keywordInput.trim() && !formData.triggerKeywords?.includes(keywordInput.trim())) {
      setFormData(prev => ({
        ...prev,
        triggerKeywords: [...(prev.triggerKeywords || []), keywordInput.trim()]
      }));
      setKeywordInput('');
    }
  };

  const handleRemoveKeyword = (keyword: string) => {
    setFormData(prev => ({
      ...prev,
      triggerKeywords: (prev.triggerKeywords || []).filter(k => k !== keyword)
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold">
            {isEditing
              ? (t('aiAgents.dynamicContext.editContext') || 'Edit Context')
              : (t('aiAgents.dynamicContext.addContext') || 'Add Context')}
          </h2>
          <button type="button" onClick={onClose} className="p-1 hover:bg-muted rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-4">
          {/* Key and Name */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>{t('aiAgents.dynamicContext.key') || 'Context Key'}</Label>
              <input
                type="text"
                value={formData.contextKey}
                onChange={e => setFormData(prev => ({ ...prev, contextKey: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '') }))}
                className="w-full mt-1 px-3 py-2 bg-background border border-input rounded-md"
                placeholder="e.g., FEATURES"
              />
            </div>
            <div>
              <Label>{t('aiAgents.dynamicContext.name') || 'Context Name'}</Label>
              <input
                type="text"
                value={formData.contextName}
                onChange={e => setFormData(prev => ({ ...prev, contextName: e.target.value }))}
                className="w-full mt-1 px-3 py-2 bg-background border border-input rounded-md"
                placeholder="e.g., Product Features"
              />
            </div>
          </div>

          {/* Content */}
          <div>
            <div className="flex items-center justify-between">
              <Label>{t('aiAgents.dynamicContext.content') || 'Content'}</Label>
              <span className={`text-xs ${estimateTokens(formData.contextContent) > 120 ? 'text-red-500' : 'text-muted-foreground'}`}>
                ~{estimateTokens(formData.contextContent)} tokens (max 120)
              </span>
            </div>
            <textarea
              value={formData.contextContent}
              onChange={e => setFormData(prev => ({ ...prev, contextContent: e.target.value }))}
              className="w-full mt-1 px-3 py-2 bg-background border border-input rounded-md h-32 resize-y"
              placeholder="Content to inject when triggered..."
            />
          </div>

          {/* Keywords */}
          <div>
            <Label>{t('aiAgents.dynamicContext.triggerKeywords') || 'Trigger Keywords'}</Label>
            <div className="flex items-center gap-2 mt-1">
              <input
                type="text"
                value={keywordInput}
                onChange={e => setKeywordInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddKeyword())}
                className="flex-1 px-3 py-2 bg-background border border-input rounded-md"
                placeholder="Type keyword and press Enter"
              />
              <button type="button" onClick={handleAddKeyword} className="p-2 bg-primary text-primary-foreground rounded-md">
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {formData.triggerKeywords?.map(kw => (
                <span key={kw} className="flex items-center gap-1 text-sm bg-primary/10 text-primary px-2 py-1 rounded">
                  {kw}
                  <button type="button" onClick={() => handleRemoveKeyword(kw)}>
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Priority and Reuse Policy */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>{t('aiAgents.dynamicContext.priority') || 'Priority (1-100)'}</Label>
              <input
                type="number"
                min={1}
                max={100}
                value={formData.priority}
                onChange={e => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) || 50 }))}
                className="w-full mt-1 px-3 py-2 bg-background border border-input rounded-md"
              />
            </div>
            <div>
              <Label>{t('aiAgents.dynamicContext.reusePolicy') || 'Reuse Policy'}</Label>
              <select
                value={formData.reusePolicy}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onChange={e => setFormData(prev => ({ ...prev, reusePolicy: e.target.value as any }))}
                className="w-full mt-1 px-3 py-2 bg-background border border-input rounded-md"
              >
                <option value="reusable">Reusable (can inject multiple times)</option>
                <option value="once_per_session">Once per session</option>
              </select>
            </div>
          </div>

          {/* Enabled */}
          <div className="flex items-center gap-2">
            <Toggle
              value={formData.enabled ?? true}
              onChange={val => setFormData(prev => ({ ...prev, enabled: val }))}
            />
            <Label>{t('common.enabled') || 'Enabled'}</Label>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-border">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm hover:bg-muted rounded-md">
            {t('common.cancel') || 'Cancel'}
          </button>
          <button
            type="button"
            onClick={onSave}
            className="px-4 py-2 text-sm bg-primary text-primary-foreground hover:bg-primary/90 rounded-md"
          >
            {t('common.save') || 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
